import React from "react";
import { Link } from "react-router-dom";
import "./styles/HowItWorks.css";

const HowItWorks = () => {
  return (
    <div className="how-it-works-page">
      {/* Hero Header */}
      <section className="how-it-works-header">
        <div className="how-it-works-container">
          <div className="header-badge">✨ AI-Powered Travel Management</div>
          <h1>How ExploreEase Works</h1>
          <h2>Your Complete Travel Companion from Planning to Memories</h2>
          <p>
            ExploreEase handles everything after you book, so you can focus on
            the adventure. Organize trips, track budgets, and preserve
            experiences—all powered by AI.
          </p>
          <div className="cta-buttons">
            <Link to="/signup" className="btn-get-started">
              Get Started Free
              <span className="arrow">→</span>
            </Link>
            <Link to="/pricing" className="btn-view-pricing">
              View Pricing
            </Link>
          </div>
          <div className="trust-badges">
            <div className="trust-item">
              <span className="trust-icon">✓</span>
              <span>No credit card required</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">✓</span>
              <span>Full AI features on free plan</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">✓</span>
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
        <div className="header-image-wrapper">
          <div className="image-decoration"></div>
          <img src="/Images/how-it-works.svg" alt="How ExploreEase Works" />
        </div>
      </section>

      {/* Process Overview */}
      <section className="process-overview">
        <div className="process-header">
          <h2>Your Complete Travel Journey</h2>
          <p>Every step of your adventure, perfectly organized</p>
        </div>
        <div className="process-timeline">
          <div className="timeline-item">
            <div className="timeline-dot">1</div>
            <div className="timeline-label">Plan</div>
          </div>
          <div className="timeline-line"></div>
          <div className="timeline-item">
            <div className="timeline-dot">2</div>
            <div className="timeline-label">Prepare</div>
          </div>
          <div className="timeline-line"></div>
          <div className="timeline-item">
            <div className="timeline-dot">3</div>
            <div className="timeline-label">Travel</div>
          </div>
          <div className="timeline-line"></div>
          <div className="timeline-item">
            <div className="timeline-dot">4</div>
            <div className="timeline-label">Remember</div>
          </div>
        </div>
      </section>

      <div className="steps-container">
        {/* Step 1 - Plan Your Trip */}
        <div className="step">
          <div className="step-badge">
            <span className="badge-number">01</span>
            <span className="badge-label">Before You Go</span>
          </div>
          <div className="step-content">
            <div className="step-icon-large">🗺️</div>
            <h2>Plan Your Trip</h2>
            <p className="step-description">
              Create your trip with destinations, dates, and travelers. Get
              AI-generated packing lists based on weather and activities,
              download a comprehensive travel playbook PDF with visa
              requirements and local customs, and organize your custom
              itinerary.
            </p>
            <div className="feature-tags">
              <span className="tag">AI Packing Lists</span>
              <span className="tag">Travel Playbooks</span>
              <span className="tag">Visa Info</span>
            </div>
            <ul className="step-features">
              <li>
                <strong>Create your trip</strong> with destinations, dates, and
                participants
              </li>
              <li>
                <strong>AI-powered packing suggestions</strong> based on weather
                and activities
              </li>
              <li>
                <strong>Download travel playbook PDF</strong> with visa info,
                customs, safety tips
              </li>
              <li>
                <strong>Build custom itinerary</strong> organized by day
              </li>
            </ul>
          </div>
          <div className="step-image">
            <div className="image-frame">
              <img src="/Images/step1.svg" alt="Plan Your Trip" />
            </div>
          </div>
        </div>

        {/* Step 2 - Get Ready */}
        <div className="step-reverse">
          <div className="step-badge step-badge-alt">
            <span className="badge-number">02</span>
            <span className="badge-label">Get Ready</span>
          </div>
          <div className="step-content">
            <div className="step-icon-large">🎯</div>
            <h2>Book Activities & Prepare</h2>
            <p className="step-description">
              Book activities and experiences directly through the app, learn
              essential phrases for your destination, play trivia to discover
              local culture, and set your budget with live exchange rate
              tracking.
            </p>
            <div className="feature-tags">
              <span className="tag tag-blue">Activity Booking</span>
              <span className="tag tag-blue">Key Phrases</span>
              <span className="tag tag-blue">Exchange Rates</span>
            </div>
            <ul className="step-features">
              <li>
                <strong>Book activities in-app</strong> tours, restaurants,
                experiences
              </li>
              <li>
                <strong>Learn key phrases</strong> tailored to your destination
              </li>
              <li>
                <strong>Play travel trivia</strong> to discover fun facts and
                culture
              </li>
              <li>
                <strong>Set budget & track exchange rates</strong> in real-time
              </li>
            </ul>
          </div>
          <div className="step-image">
            <div className="image-frame image-frame-alt">
              <img src="/Images/step2.svg" alt="Prepare for Trip" />
            </div>
          </div>
        </div>

        {/* Step 3 - During Your Trip */}
        <div className="step">
          <div className="step-badge">
            <span className="badge-number">03</span>
            <span className="badge-label">On the Road</span>
          </div>
          <div className="step-content">
            <div className="step-icon-large">✈️</div>
            <h2>Manage Your Trip on the Go</h2>
            <p className="step-description">
              Track expenses instantly, split bills with your travel group,
              receive daily personalized insights, and capture memories with
              guided journal prompts. Everything syncs in real-time so you never
              miss a moment.
            </p>
            <div className="feature-tags">
              <span className="tag">Expense Tracking</span>
              <span className="tag">Daily Insights</span>
              <span className="tag">Journal Prompts</span>
            </div>
            <ul className="step-features">
              <li>
                <strong>Track expenses on the go</strong> with quick entry and
                auto-categorization
              </li>
              <li>
                <strong>Split bills automatically</strong> and see who owes whom
              </li>
              <li>
                <strong>Receive daily AI insights</strong> personalized to your
                itinerary
              </li>
              <li>
                <strong>Journal prompts</strong> to capture memories while
                they're fresh
              </li>
            </ul>
          </div>
          <div className="step-image">
            <div className="image-frame">
              <img src="/Images/step3.svg" alt="Travel Management" />
            </div>
          </div>
        </div>

        {/* Step 4 - After Your Trip */}
        <div className="step-reverse">
          <div className="step-badge step-badge-alt">
            <span className="badge-number">04</span>
            <span className="badge-label">Back Home</span>
          </div>
          <div className="step-content">
            <div className="step-icon-large">📸</div>
            <h2>Relive Your Memories</h2>
            <p className="step-description">
              Your journal entries, photos, and experiences automatically
              organize into a beautiful timeline. Rate your favorite moments
              with 5-star ratings and mood tracking, then relive your adventure
              anytime.
            </p>
            <div className="feature-tags">
              <span className="tag tag-blue">Auto Timeline</span>
              <span className="tag tag-blue">Photo Gallery</span>
              <span className="tag tag-blue">Mood Tracking</span>
            </div>
            <ul className="step-features">
              <li>
                <strong>Auto-organized timeline</strong> of your entire journey
              </li>
              <li>
                <strong>Journal with unlimited photos</strong> and guided
                prompts
              </li>
              <li>
                <strong>Mood tracking & 5-star ratings</strong> for every
                experience
              </li>
              <li>
                <strong>Search and filter memories</strong> to relive any moment
              </li>
            </ul>
          </div>
          <div className="step-image">
            <div className="image-frame image-frame-alt">
              <img src="/Images/step4.svg" alt="Memory Timeline" />
            </div>
          </div>
        </div>
      </div>

      {/* AI Features Overview */}
      <section className="ai-features-section">
        <h2>AI-Powered Features</h2>
        <p className="section-subtitle">
          Smart automation that works in the background
        </p>

        <div className="ai-features-grid">
          <div className="ai-feature-card">
            <div className="ai-feature-icon">🎒</div>
            <h3>Smart Packing Suggestions</h3>
            <p>
              Personalized packing lists based on your destination's weather and
              your planned activities
            </p>
          </div>

          <div className="ai-feature-card">
            <div className="ai-feature-icon">📚</div>
            <h3>Travel Playbook Generation</h3>
            <p>
              Comprehensive PDFs with visa requirements, local customs, safety
              tips, and essential info
            </p>
          </div>

          <div className="ai-feature-card">
            <div className="ai-feature-icon">💰</div>
            <h3>Auto Expense Categorization</h3>
            <p>
              Expenses automatically categorize and organize for easy budget
              tracking
            </p>
          </div>

          <div className="ai-feature-card">
            <div className="ai-feature-icon">💡</div>
            <h3>Daily Travel Insights</h3>
            <p>
              Receive personalized tips based on your itinerary and current
              location
            </p>
          </div>

          <div className="ai-feature-card">
            <div className="ai-feature-icon">📖</div>
            <h3>Memory Timeline Builder</h3>
            <p>
              Your journal entries and photos auto-organize into a beautiful
              chronological timeline
            </p>
          </div>

          <div className="ai-feature-card">
            <div className="ai-feature-icon">💬</div>
            <h3>Terra AI Assistant</h3>
            <p>
              Need help? Chat with Terra for quick answers, expense tracking, or
              destination questions
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-header">
          <h2>ExploreEase by the Numbers</h2>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">10K+</div>
            <div className="stat-label">Trips Organized</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">50K+</div>
            <div className="stat-label">Memories Captured</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">95%</div>
            <div className="stat-label">User Satisfaction</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">24/7</div>
            <div className="stat-label">AI Availability</div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="cta-box">
          <div className="cta-icon">✈️</div>
          <h2>Ready to Transform Your Travel Experience?</h2>
          <p>
            Start with one trip completely free. Experience AI-powered planning,
            real-time expense tracking, and automatic memory organization.
            Upgrade anytime for unlimited trips.
          </p>
          <div className="cta-buttons">
            <Link to="/signup" className="btn-cta-primary">
              Start Your Free Trip
              <span className="arrow">→</span>
            </Link>
            <Link to="/pricing" className="btn-cta-secondary">
              View Plans & Pricing
            </Link>
          </div>
          <p className="cta-note">
            <span className="note-icon">🔒</span>
            No credit card required • Full AI features • Cancel anytime
          </p>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
